// Mission status model normalization
// Centralized definitions, labels, DB mapping, and transitions

export type MissionStatus = 'Draft' | 'Planned' | 'InProgress' | 'Done';

export const MISSION_STATUS_ORDER: MissionStatus[] = ['Draft', 'Planned', 'InProgress', 'Done'];

export function isValidMissionStatus(status: unknown): status is MissionStatus {
	return typeof status === 'string' && ['Draft', 'Planned', 'InProgress', 'Done'].includes(status as MissionStatus);
}

export function labelForMissionStatus(status: MissionStatus): string {
	return status === 'Draft'
		? 'Entwurf'
		: status === 'Planned'
		? 'Geplant'
		: status === 'InProgress'
		? 'In Arbeit'
		: 'Abgeschlossen';
}

// DB mapping: canonical values are snake_case in DB
export function mapDbStatusToUi(status: unknown): MissionStatus {
	if (typeof status !== 'string') return 'Planned';
	switch (status) {
		case 'draft':
		case 'Draft':
			return 'Draft';
		case 'planned':
		case 'Planned':
			return 'Planned';
		case 'in_progress':
		case 'InProgress':
			return 'InProgress';
		case 'completed':
		case 'Done':
			return 'Done';
		case 'cancelled':
			// Treated as planned for now; UI does not expose a distinct state
			return 'Planned';
		default:
			return 'Planned';
	}
}

export function mapUiStatusToDb(status: MissionStatus | undefined): string | undefined {
	if (!status) return undefined;
	switch (status) {
		case 'Draft':
			return 'draft';
		case 'Planned':
			return 'planned';
		case 'InProgress':
			return 'in_progress';
		case 'Done':
			return 'completed';
		default:
			return undefined;
	}
}

export function cycleMissionStatus(current: MissionStatus): MissionStatus {
	const order = MISSION_STATUS_ORDER;
	const currentIndex = order.indexOf(current);
	const nextIndex = (currentIndex + 1) % order.length;
	return order[nextIndex];
}

// Mission lead statuses remain simple and UI-level for now
export type MissionLeadStatus = 'Planned' | 'Visited' | 'Skipped';
export function isValidMissionLeadStatus(status: unknown): status is MissionLeadStatus {
	return typeof status === 'string' && ['Planned', 'Visited', 'Skipped'].includes(status);
}
